import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckDouble, faEnvelopeOpenText } from '@fortawesome/free-solid-svg-icons';
import MUIDataTable from 'mui-datatables';
import PageHeader from '../Components/Common/PageHeader';
import ScoreCard from '../Components/Common/ScoreCard';
import BarChart from '../Components/Common/Charts/BarChart';
import Loader from '../Components/Loader';

const ProductandServiceMetrics = () => {
  const [requestData, setRequestData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvedCount, setApprovedCount] = useState(0);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get('/api/ProductCatalogueRequest/getRequestDetails');
        setRequestData(res.data);

        setApprovedCount(
          res.data.filter((item) => item?.approvalStatus?.toLowerCase() === 'approved').length,
        );
      } catch (error) {
        console.error('Error fetching product/service data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const { categories, series } = useMemo(() => {
    const today = new Date();
    const days = Array.from({ length: 15 }, (_, i) => {
      const d = new Date(today);
      d.setDate(today.getDate() - (14 - i));
      // format to display on axis
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });
    const totals = {}; const
      approved = {};
    requestData.forEach((item) => {
      if (!item.createdAt) return;
      const d = new Date(item.createdAt);
      const key = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      totals[key] = (totals[key] || 0) + 1;
      if (item.approvalStatus?.toLowerCase() === 'approved') {
        approved[key] = (approved[key] || 0) + 1;
      }
    });
    const requestsSeries = days.map((d) => totals[d] || 0);
    const approvedSeries = days.map((d) => approved[d] || 0);
    return {
      categories: days,
      series: [
        { name: 'Requests', data: requestsSeries },
        { name: 'Approved', data: approvedSeries },
      ],
    };
  }, [requestData]);

  const chartTitle = 'Product and Service Requests Over Time';

  const totalRequests = requestData.length;

  // Dummy data for the table
  const data = requestData.map((item) => [
    // String(index + 1).padStart(3, '0'), // Request ID (001, 002...)
    item.title || 'N/A',
    item.category || 'N/A',
    item.requestor || 'N/A',
    item.productOwner || 'N/A',
    item.createdAt ? new Date(item.createdAt).toISOString().split('T')[0] : 'N/A', // Format date to yyyy-mm-dd
    item.updatedAt ? new Date(item.updatedAt).toISOString().split('T')[0] : 'N/A',
    item.approvalStatus || 'N/A',
  ]);

  const getColumn = () => [
    {
      name: 'Product/Service Name',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'Category',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'Requestor Name',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'Product Owner',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'Request Date',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'Updated Date',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'Status',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          let badgeClass = 'badge ';
          switch (value) {
            case 'Approved':
              badgeClass += 'bg-success';
              break;
            case 'Pending':
              badgeClass += 'bg-warning';
              break;
            case 'Rejected':
              badgeClass += 'bg-danger';
              break;
            default:
              badgeClass += 'bg-secondary';
          }
          return <span className={badgeClass}>{value}</span>;
        },
      },
    },
  ];

  const options = {
    filterType: 'checkbox',
    responsive: 'standard',
    selectableRows: 'none',
    elevation: 0,
    rowsPerPageOptions: [10, 25, 50, 100],
    count: requestData.length,
  };
  if (loading) return <Loader />;

  return (
    <>
      <div className="wrapper">
        <div className="main">

          <div className="page-content">
            <PageHeader pageTitle="Product and Service Metrics" />
            <div className="d-flex">
              <ScoreCard icon={<FontAwesomeIcon icon={faEnvelopeOpenText} />} title="Total Requests" count={totalRequests} infoText="Total number of requests" />
              <ScoreCard
                icon={<FontAwesomeIcon icon={faCheckDouble} />}
                title="Approved Requests"
                count={approvedCount}
                infoText="Total number of approved requests"
              />

            </div>
            <div className="card mb-3">
              <div className="card-body">
                <BarChart series={series} categories={categories} title={chartTitle} />
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <MUIDataTable
                    title="Product and Service Requests"
                    data={data}
                    columns={getColumn()}
                    options={options}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductandServiceMetrics;
